import { Ionicons } from '@expo/vector-icons';
import { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { DisputeInfo } from '@/lib/disputes';
import { disputeTagLabel } from '@/lib/disputes';
import { formatDate, genderedArticle } from '@/lib/format';

type Props = {
  dispute: DisputeInfo;
  otherName: string;
};

export function DisputeStatusBanner({ dispute, otherName }: Props) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const expiresDate = formatDate(dispute.expires_at);

  if (dispute.status === 'withdrawn') {
    return (
      <View style={[styles.banner, styles.bannerSuccess]}>
        <Ionicons name="checkmark-circle-outline" size={18} color={colors.success} />
        <Text style={styles.successText}>{t('dispute.banner_withdrawn')}</Text>
      </View>
    );
  }

  if (dispute.status === 'auto_cancelled') {
    return (
      <View style={[styles.banner, styles.bannerDanger]}>
        <Ionicons name="time-outline" size={18} color={colors.danger} />
        <Text style={styles.dangerText}>
          {dispute.i_am_initiator
            ? t('dispute.banner_autoCancelled_initiator', { article: genderedArticle(otherName), name: otherName })
            : t('dispute.banner_autoCancelled_respondent')}
        </Text>
      </View>
    );
  }

  if (dispute.status === 'mutual_resolution_proposed' && dispute.mutual_resolution_proposed_by_me) {
    return (
      <View style={[styles.banner, styles.bannerInfo]}>
        <Ionicons name="hourglass-outline" size={18} color={colors.primary} />
        <View style={styles.textBlock}>
          <Text style={styles.infoText}>{t('dispute.banner_waitingMutualDecision', { name: otherName })}</Text>
          {!!dispute.mutual_resolution_expires_at && (
            <Text style={styles.subText}>
              {t('dispute.mutual_deadline_until', { date: formatDate(dispute.mutual_resolution_expires_at) })}
            </Text>
          )}
        </View>
      </View>
    );
  }

  if (dispute.status === 'jury_review' || dispute.status === 'voting') {
    return (
      <View style={[styles.banner, styles.bannerInfo]}>
        <Ionicons name="people-outline" size={18} color={colors.primary} />
        <View style={styles.textBlock}>
          <Text style={styles.infoText}>{t('dispute.banner_juryReviewTitle')}</Text>
          <Text style={styles.subText}>
            {t('dispute.banner_juryReviewBody', { votes: dispute.jury_votes_cast, total: dispute.jury_size })}
          </Text>
          {dispute.stake_locked && dispute.i_am_initiator && (
            <Text style={styles.subText}>{t('dispute.banner_stakeLocked', { amount: dispute.stake_amount })}</Text>
          )}
        </View>
      </View>
    );
  }

  if (dispute.status === 'admin_review') {
    return (
      <View style={[styles.banner, styles.bannerWarning]}>
        <Ionicons name="shield-outline" size={18} color={colors.warning} />
        <Text style={styles.warningText}>{t('dispute.banner_adminReview')}</Text>
      </View>
    );
  }

  if (dispute.status === 'resolved') {
    // Εθελοντική αποδοχή από τον respondent — ελέγχεται ΠΡΩΤΑ
    if (dispute.respondent_decision === 'accepted') {
      return (
        <View style={[styles.banner, styles.bannerWarning]}>
          <Ionicons name="checkmark-circle-outline" size={18} color={colors.warning} />
          <Text style={styles.warningText}>
            {dispute.i_am_initiator
              ? t('dispute.banner_acceptedByRespondent_initiator', { article: genderedArticle(otherName), name: otherName })
              : t('dispute.banner_acceptedByRespondent_self')}
          </Text>
        </View>
      );
    }
    if (dispute.outcome === 'deadlock') {
      const myLoss = Math.abs(dispute.my_penalty ?? 0.3);
      const otherLoss = Math.abs(dispute.other_penalty ?? 0.4);
      return (
        <View style={[styles.banner, styles.bannerDanger]}>
          <Ionicons name="close-circle-outline" size={18} color={colors.danger} />
          <Text style={styles.dangerText}>
            {t('dispute.banner_deadlock', { myLoss, article: genderedArticle(otherName).toLowerCase(), name: otherName, otherLoss })}
          </Text>
        </View>
      );
    }
    if (dispute.outcome === 'jury_favor_initiator' || dispute.outcome === 'jury_favor_accused') {
      const iWon = (dispute.outcome === 'jury_favor_initiator') === dispute.i_am_initiator;
      // Το ενέχυρο (stake) το δεσμεύει ΜΟΝΟ ο καταγγέλλων — ο κατηγορούμενος
      // δεν έχει ποτέ μελάνια σε κίνδυνο, άρα δεν πρέπει να βλέπει μήνυμα περί
      // απώλειας/μη-επιστροφής ενεχύρου, όποιο κι αν είναι το αποτέλεσμα.
      const juryText = dispute.i_am_initiator
        ? (iWon ? t('dispute.banner_juryWon', { amount: dispute.stake_amount }) : t('dispute.banner_juryLost', { amount: dispute.stake_amount }))
        : (iWon ? t('dispute.banner_juryWon_accused') : t('dispute.banner_juryLost_accused'));
      return (
        <View style={[styles.banner, iWon ? styles.bannerSuccess : styles.bannerDanger]}>
          <Ionicons name={iWon ? 'ribbon-outline' : 'close-circle-outline'} size={18} color={iWon ? colors.success : colors.danger} />
          <View style={styles.textBlock}>
            <Text style={iWon ? styles.successText : styles.dangerText}>{juryText}</Text>
            {dispute.malicious_flagged && !iWon && (
              <Text style={styles.dangerText}>{t('dispute.banner_maliciousStrike')}</Text>
            )}
          </View>
        </View>
      );
    }
    if (dispute.outcome === 'penalty_respondent') {
      return (
        <View style={[styles.banner, styles.bannerDanger]}>
          <Ionicons name="close-circle-outline" size={18} color={colors.danger} />
          <Text style={styles.dangerText}>
            {dispute.i_am_initiator
              ? t('dispute.banner_penaltyRespondent_initiator', { article: genderedArticle(otherName).toLowerCase(), name: otherName })
              : t('dispute.banner_penaltyRespondent_respondent')}
          </Text>
        </View>
      );
    }
  }

  // status === 'open'
  if (dispute.i_am_initiator) {
    if (dispute.both_submitted) {
      return (
        <View style={[styles.banner, styles.bannerInfo]}>
          <Ionicons name="eye-outline" size={18} color={colors.primary} />
          <View style={styles.textBlock}>
            <Text style={styles.infoText}>{t('dispute.banner_bothSubmitted')}</Text>
          </View>
        </View>
      );
    }
    if (dispute.respondent_decision === null) {
      return (
        <View style={[styles.banner, styles.bannerWarning]}>
          <Ionicons name="snow-outline" size={18} color={colors.warning} />
          <View style={styles.textBlock}>
            <Text style={styles.warningText}>{t('dispute.banner_frozenTitle')}</Text>
            <Text style={styles.subText}>{t('dispute.banner_waitingOther', { name: otherName, date: expiresDate })}</Text>
            {dispute.my_tags.length > 0 && (
              <View style={styles.myTags}>
                {dispute.my_tags.map((k) => (
                  <View key={k} style={styles.myTagPill}>
                    <Text style={styles.myTagText}>{disputeTagLabel(k)}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      );
    }
  } else {
    // i am respondent
    if (dispute.both_submitted) {
      return (
        <View style={[styles.banner, styles.bannerInfo]}>
          <Ionicons name="eye-outline" size={18} color={colors.primary} />
          <Text style={styles.infoText}>{t('dispute.banner_bothSubmittedWaiting', { name: otherName })}</Text>
        </View>
      );
    }
    if (!dispute.i_have_submitted) {
      return (
        <View style={[styles.banner, styles.bannerDanger]}>
          <Ionicons name="alert-circle-outline" size={18} color={colors.danger} />
          <View style={styles.textBlock}>
            <Text style={styles.dangerText}>{t('dispute.banner_pendingResponseTitle')}</Text>
            <Text style={styles.subText}>{t('dispute.banner_respondDeadline', { date: expiresDate })}</Text>
          </View>
        </View>
      );
    }
    return (
      <View style={[styles.banner, styles.bannerWarning]}>
        <Ionicons name="snow-outline" size={18} color={colors.warning} />
        <Text style={styles.warningText}>{t('dispute.banner_statementSubmitted', { name: otherName })}</Text>
      </View>
    );
  }

  return null;
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    banner: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
      borderRadius: radius.md,
      padding: spacing.md,
    },
    bannerInfo: {
      backgroundColor: 'rgba(201,168,67,0.10)',
      borderWidth: 1,
      borderColor: 'rgba(201,168,67,0.25)',
    },
    bannerWarning: {
      backgroundColor: 'rgba(245,158,11,0.10)',
      borderWidth: 1,
      borderColor: 'rgba(245,158,11,0.30)',
    },
    bannerDanger: {
      backgroundColor: 'rgba(239,68,68,0.10)',
      borderWidth: 1,
      borderColor: 'rgba(239,68,68,0.30)',
    },
    bannerSuccess: {
      backgroundColor: 'rgba(34,197,94,0.10)',
      borderWidth: 1,
      borderColor: 'rgba(34,197,94,0.30)',
    },
    textBlock: { flex: 1, gap: 4 },
    infoText: { flex: 1, color: colors.primary, fontSize: fontSize.sm, lineHeight: 19, fontWeight: '600' },
    warningText: { color: colors.warning, fontSize: fontSize.sm, lineHeight: 19, fontWeight: '600' },
    dangerText: { flex: 1, color: colors.danger, fontSize: fontSize.sm, lineHeight: 19, fontWeight: '600' },
    successText: { flex: 1, color: colors.success, fontSize: fontSize.sm, lineHeight: 19, fontWeight: '600' },
    subText: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 18 },
    myTags: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 4 },
    myTagPill: {
      borderRadius: radius.full,
      borderWidth: 1,
      borderColor: colors.primary,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
    },
    myTagText: { color: colors.primary, fontSize: 12, fontWeight: '600' },
  });
}
